import type { SequenceRead } from './sequenceHead.ts'
import { bestPrefixEditDistance, toMasks } from './iupac.ts'
import { stripAdapter } from './primerRegistry.ts'
import type { PrimerPair } from './primerTrimDetection.ts'

export type MultiplexVerdict = 'MULTIPLEXED' | 'SINGLE_SAMPLE' | 'UNKNOWN'

export type MultiplexResult = {
  verdict: MultiplexVerdict
  /** Share of the located reads carrying the most common leading sequence in front of the gene primer. */
  topPrefixShare: number
  locatedReads: number
  reason: string
}

/** Inline sample tags seen in prod run up to 12 bases, plus a few bases of spacer. */
const MAX_PREFIX = 16
const MIN_LOCATED = 25
/* prod uploads: one leading sequence on >=70% of a per-sample file's reads, <=19% of a multiplexed one's — one outlier each */
const MULTIPLEXED_AT_MOST = 0.35
const SINGLE_AT_LEAST = 0.6

type Located = { masks: number[]; maxErrors: number }

const prepare = (primer: string): Located | null => {
  const clean = stripAdapter(primer)
  if (clean.length < 12) {
    return null
  }
  return { masks: toMasks(clean), maxErrors: Math.floor(clean.length * 0.15) }
}

/** Where the best-matching gene primer starts in the read, or -1. */
const primerStart = (read: string, primers: Located[]): number => {
  let best = Number.POSITIVE_INFINITY
  let at = -1
  for (const primer of primers) {
    for (let s = 0; s <= MAX_PREFIX && s + primer.masks.length <= read.length; s++) {
      const distance = bestPrefixEditDistance(primer.masks, read.slice(s), 0, primer.maxErrors)
      if (distance <= primer.maxErrors && distance < best) {
        best = distance
        at = s
      }
    }
  }
  return at
}

/**
 * Whether one file holds several samples told apart by inline tags: the bases in front of the gene
 * primer are then spread over many tags instead of one. `genePrimers` must be the bare gene primers
 * (the registered sequences), not a customer's tagged ones, or the tag itself would be matched away.
 *
 * Only inline-tag multiplexing is visible this way. Illumina-index multiplexing is not: in every prod
 * multiplexed upload the index field of the headers was a single value.
 */
export const detectMultiplexing = (reads: readonly SequenceRead[], genePrimers: PrimerPair): MultiplexResult => {
  const primers = [prepare(genePrimers.forward), prepare(genePrimers.reverse)].flatMap((p) => (p ? [p] : []))
  if (primers.length === 0) {
    return { verdict: 'UNKNOWN', topPrefixShare: 0, locatedReads: 0, reason: 'no usable gene primer' }
  }
  const prefixes = new Map<string, number>()
  let located = 0
  for (const read of reads) {
    const start = primerStart(read.sequence, primers)
    if (start >= 0) {
      located++
      const prefix = read.sequence.slice(0, start)
      prefixes.set(prefix, (prefixes.get(prefix) ?? 0) + 1)
    }
  }
  if (located < MIN_LOCATED) {
    return { verdict: 'UNKNOWN', topPrefixShare: 0, locatedReads: located, reason: `gene primer found on only ${located} reads` }
  }
  const topPrefixShare = Math.max(...prefixes.values()) / located
  if (topPrefixShare <= MULTIPLEXED_AT_MOST) {
    return {
      verdict: 'MULTIPLEXED',
      topPrefixShare,
      locatedReads: located,
      reason: `${prefixes.size} different leading sequences before the primer`
    }
  }
  if (topPrefixShare >= SINGLE_AT_LEAST) {
    return { verdict: 'SINGLE_SAMPLE', topPrefixShare, locatedReads: located, reason: 'one leading sequence before the primer' }
  }
  return { verdict: 'UNKNOWN', topPrefixShare, locatedReads: located, reason: 'leading sequences neither uniform nor spread' }
}
